import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, ChevronLeft, ChevronRight, GripVertical, Pencil, RefreshCw, Save, Search, Settings, UserPlus, X } from 'lucide-react'

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  )
}

import { Button } from '@/components/ui/button'
import type { Event, SignUp } from './types'

const EXTRA_ROLE_OPTIONS = [
  { id: 'at',              label: 'AT',           emoji: '/roles/at.webp' },
  { id: 'mg',              label: 'MG',           emoji: '/roles/mg.webp' },
  { id: 'supply',          label: 'Supply',       emoji: '/roles/supply.webp' },
  { id: 'garrison',        label: 'Garrison',     emoji: '/roles/garrison.webp' },
  { id: 'engineer',        label: 'Engineer',     emoji: '/roles/engineer.webp' },
  { id: 'transport_truck', label: 'Transport',    emoji: '/roles/transport_truck.webp' },
  { id: 'supply_truck',    label: 'Supply Truck', emoji: '/roles/supply_truck.webp' },
]

const ROLE_ICONS: Record<string, string> = {
  sl: '/roles/sl.webp', infantry: '/roles/infantry.webp', tc: '/roles/tc.webp',
  driver: '/roles/driver.webp', flareman: '/roles/flareman.webp', sniper: '/roles/sniper.webp',
  commander: '/roles/commander.webp', artillery: '/roles/artillery.webp', supply: '/roles/supply.webp',
  engineer: '/roles/engineer.webp', at: '/roles/at.webp', mg: '/roles/mg.webp',
  garrison: '/roles/garrison.webp', transport_truck: '/roles/transport_truck.webp',
  supply_truck: '/roles/supply_truck.webp', gunner: '/roles/gunner.webp',
}

interface SlotRule { role: string; label: string; fixed?: boolean; min?: number; max?: number }

const SQUAD_TYPE_CONFIGS: Record<string, SlotRule[]> = {
  infantry:  [
    { role: 'sl',       label: 'Squad Leader', min: 1, max: 4 },
    { role: 'infantry', label: 'Infantry',     min: 0, max: 10 },
  ],
  armour:    [
    { role: 'tc',     label: 'Tank Commander', fixed: true },
    { role: 'driver', label: 'Crew',           min: 0, max: 2 },
  ],
  recon:     [
    { role: 'flareman', label: 'Flareman', fixed: true },
    { role: 'sniper',   label: 'Sniper',   min: 0, max: 1 },
  ],
  commander: [{ role: 'commander', label: 'Commander', fixed: true }],
  artillery: [
    { role: 'artillery', label: 'Artillery', fixed: true },
    { role: 'supply',    label: 'Supply',    min: 0, max: 1 },
    { role: 'engineer',  label: 'Engineer',  min: 0, max: 1 },
  ],
}

interface PresetDef { type: string; label: string; icon: string; defaultSlots: { role: string }[] }

const SQUAD_PRESETS: PresetDef[] = [
  { type: 'infantry',  label: 'Infantry',  icon: '/roles/infantry.webp',
    defaultSlots: [{role:'sl'},{role:'sl'},{role:'infantry'},{role:'infantry'},{role:'infantry'},{role:'infantry'}] },
  { type: 'armour',    label: 'Armour',    icon: '/roles/armour.webp',
    defaultSlots: [{role:'tc'},{role:'driver'},{role:'driver'}] },
  { type: 'recon',     label: 'Recon',     icon: '/roles/recon.webp',
    defaultSlots: [{role:'flareman'},{role:'sniper'}] },
  { type: 'commander', label: 'Commander', icon: '/roles/commander.webp',
    defaultSlots: [{role:'commander'}] },
  { type: 'artillery', label: 'Artillery', icon: '/roles/artillery.webp',
    defaultSlots: [{role:'artillery'}] },
]

const ACTIVE_CLASSES = new Set(['Inf','Tank','Recon','Comm','Arty','Artillery','Streamer','infantry','armour','recon','commander','artillery','tank'])
const BENCH_CLASSES  = new Set(['Bench'])

interface Slot  { id: string; role: string; userId: string | null }
interface Squad { id: string; name: string; type: string; icon: string; side: 'axis' | 'allies'; slots: Slot[] }

interface MemberOption { id: string; username: string; display_name: string; avatar_url: string | null; team: string }
interface ContextMenu  { userId: string; x: number; y: number }
interface DraggingPlayer { userId: string; from: string | null; slotId?: string }

function makeSlots(defs: { role: string }[]): Slot[] {
  return defs.map(d => ({ id: `s${Date.now()}_${Math.random().toString(36).slice(2)}`, role: d.role, userId: null }))
}

function makeDefaultSquads(): Squad[] {
  return [
    { id: 'ax_cmd',  name: 'Commander',  type: 'commander', side: 'axis',   icon: '/roles/commander.webp', slots: makeSlots([{role:'commander'}]) },
    { id: 'ax_inf1', name: 'Infantry 1', type: 'infantry',  side: 'axis',   icon: '/roles/infantry.webp',  slots: makeSlots([{role:'sl'},{role:'sl'},{role:'infantry'},{role:'infantry'},{role:'infantry'},{role:'infantry'}]) },
    { id: 'ax_inf2', name: 'Infantry 2', type: 'infantry',  side: 'axis',   icon: '/roles/infantry.webp',  slots: makeSlots([{role:'sl'},{role:'sl'},{role:'infantry'},{role:'infantry'},{role:'infantry'},{role:'infantry'}]) },
    { id: 'ax_arm1', name: 'Armour 1',   type: 'armour',    side: 'axis',   icon: '/roles/armour.webp',    slots: makeSlots([{role:'tc'},{role:'driver'},{role:'driver'}]) },
    { id: 'al_cmd',  name: 'Commander',  type: 'commander', side: 'allies', icon: '/roles/commander.webp', slots: makeSlots([{role:'commander'}]) },
    { id: 'al_inf1', name: 'Infantry 1', type: 'infantry',  side: 'allies', icon: '/roles/infantry.webp',  slots: makeSlots([{role:'sl'},{role:'sl'},{role:'infantry'},{role:'infantry'},{role:'infantry'},{role:'infantry'}]) },
    { id: 'al_inf2', name: 'Infantry 2', type: 'infantry',  side: 'allies', icon: '/roles/infantry.webp',  slots: makeSlots([{role:'sl'},{role:'sl'},{role:'infantry'},{role:'infantry'},{role:'infantry'},{role:'infantry'}]) },
    { id: 'al_arm1', name: 'Armour 1',   type: 'armour',    side: 'allies', icon: '/roles/armour.webp',    slots: makeSlots([{role:'tc'},{role:'driver'},{role:'driver'}]) },
  ]
}

const DAY   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
const MONTH = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function formatDate(ts: number) {
  const d = new Date(ts * 1000)
  return `${DAY[d.getDay()]}, ${d.getDate()} ${MONTH[d.getMonth()]} · ${d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}`
}

function DiscordClassIcon({ su }: { su: SignUp }) {
  const url = su.spec_emote_id && su.spec_emote_id !== 'default'
    ? `https://cdn.discordapp.com/emojis/${su.spec_emote_id}.webp?size=32`
    : su.class_emote_id && su.class_emote_id !== 'default'
      ? `https://cdn.discordapp.com/emojis/${su.class_emote_id}.webp?size=32`
      : null
  const fallbacks: Record<string,string> = { Inf:'🪖',Tank:'🛡️',Recon:'🎯',Comm:'📻',Arty:'💥',Streamer:'🎥',Bench:'🪑',Absence:'❌',Late:'⏰',Tentative:'❓' }
  if (url) return <img src={url} className="h-4 w-4 object-contain shrink-0" onError={e=>{(e.target as HTMLImageElement).style.display='none'}} />
  return <span className="text-[11px] shrink-0">{fallbacks[su.class_name]??''}</span>
}

function SlotIcon({ role, dim }: { role: string; dim?: boolean }) {
  const src = ROLE_ICONS[role]
  if (!src) return null
  return <img src={src} className={`h-4 w-4 object-contain shrink-0 transition-opacity ${dim ? 'opacity-20' : 'opacity-90'}`} onError={e=>{(e.target as HTMLImageElement).style.display='none'}} />
}

function ExtraRoleIcon({ roleId }: { roleId: string }) {
  const r = EXTRA_ROLE_OPTIONS.find(x => x.id === roleId)
  if (!r) return null
  return <img src={r.emoji} title={r.label} className="h-3.5 w-3.5 object-contain shrink-0" onError={e=>{(e.target as HTMLImageElement).style.display='none'}} />
}

function ConfirmDot({ status }: { status?: string }) {
  if (!status || status === 'pending') return <span className="h-1.5 w-1.5 rounded-full bg-white/20 shrink-0" title="Pending" />
  if (status === 'yes') return <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" title="Onaylandı" />
  return <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" title="Reddedildi" />
}

// ─── EventConfigBuilder ───────────────────────────────────────────────────────

interface Props { event: Event }

export default function EventConfigBuilder({ event }: Props) {
  const base = `/api/events/${event.id}`
  const activePlayers  = event.sign_ups.filter(s => ACTIVE_CLASSES.has(s.class_name))
  const benchPlayers   = event.sign_ups.filter(s => BENCH_CLASSES.has(s.class_name))
  const passivePlayers = event.sign_ups.filter(s => !ACTIVE_CLASSES.has(s.class_name) && !BENCH_CLASSES.has(s.class_name))

  const [customPlayers,   setCustomPlayers]   = useState<SignUp[]>([])
  const [squads,          setSquads]          = useState<Squad[]>(makeDefaultSquads)
  const [unassigned,      setUnassigned]      = useState<string[]>(activePlayers.map(s => s.user_id))
  const [benchPool,       setBenchPool]       = useState<string[]>(benchPlayers.map(s => s.user_id))
  const [substitutePool,  setSubstitutePool]  = useState<string[]>([])
  const [roleOverrides, setRoleOverrides] = useState<Record<string, string[]>>({})

  const [editingId,   setEditingId]   = useState<string | null>(null)
  const [editName,    setEditName]    = useState('')
  const [dragOver,    setDragOver]    = useState<string | null>(null)
  const [dragging,    setDragging]    = useState<DraggingPlayer | null>(null)
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null)
  const [showAddSquad,    setShowAddSquad]    = useState<'axis' | 'allies' | null>(null)
  const [configSquadId,   setConfigSquadId]   = useState<string | null>(null)
  const [configButtonRect, setConfigButtonRect] = useState<DOMRect | null>(null)
  const [saving,         setSaving]         = useState(false)
  const [saved,          setSaved]          = useState(false)
  const [loaded,         setLoaded]         = useState(false)
  const [discordPosted,  setDiscordPosted]  = useState(false)
  const [discordSending, setDiscordSending] = useState(false)
  const [discordDone,    setDiscordDone]    = useState(false)
  const [confirmations,  setConfirmations]  = useState<Record<string, string>>({})
  const [showAddMember,  setShowAddMember]  = useState(false)
  const [memberSearch,   setMemberSearch]   = useState('')
  const [unassignedSearch, setUnassignedSearch] = useState('')
  const [memberOptions,  setMemberOptions]  = useState<MemberOption[]>([])

  const nameInputRef  = useRef<HTMLInputElement | null>(null)
  const addAxisRef    = useRef<HTMLDivElement | null>(null)
  const addAlliesRef  = useRef<HTMLDivElement | null>(null)
  const configRef     = useRef<HTMLDivElement | null>(null)

  const playerMap: Record<string,SignUp> = {}
  for (const s of [...event.sign_ups, ...customPlayers]) playerMap[s.user_id] = s

  useEffect(() => {
    fetch(`${base}/discord-post`)
      .then(r => r.ok ? r.json() : { message_id: null })
      .then(d => setDiscordPosted(!!d.message_id))
      .catch(() => {})
  }, [event.id])

  useEffect(() => {
    setLoaded(false)
    fetch(`${base}/squads`)
      .then(r => r.ok ? r.json() : { squads: [], custom_players: [], role_overrides: {} })
      .then((data: { squads?: Squad[]; custom_players?: SignUp[]; role_overrides?: Record<string,string[]>; substitute_players?: string[] }) => {
        const savedSquads = data?.squads ?? []
        const cp = data?.custom_players ?? []
        const ro = data?.role_overrides ?? {}
        const savedSubs = data?.substitute_players ?? []

        if (savedSquads.length > 0 && savedSquads[0]?.slots) {
          const allDraggableIds = new Set([
            ...activePlayers.map(s => s.user_id),
            ...benchPlayers.map(s => s.user_id),
            ...cp.map(s => s.user_id),
          ])
          const validatedSquads = savedSquads.map(sq => ({
            ...sq,
            side: (sq.side ?? 'axis') as 'axis' | 'allies',
            slots: (sq.slots ?? []).map(sl => ({
              ...sl,
              userId: sl.userId && allDraggableIds.has(sl.userId) ? sl.userId : null,
            })),
          }))
          const assigned = new Set(validatedSquads.flatMap(sq => sq.slots.map(sl => sl.userId).filter(Boolean) as string[]))
          const validSubs = savedSubs.filter(id => allDraggableIds.has(id) && !assigned.has(id))
          setSquads(validatedSquads)
          setSubstitutePool(validSubs)
          const takenIds = new Set([...assigned, ...validSubs])
          setUnassigned([...activePlayers, ...cp].map(s => s.user_id).filter(id => !takenIds.has(id)))
          setBenchPool(benchPlayers.map(s => s.user_id).filter(id => !takenIds.has(id)))
        } else {
          setUnassigned(activePlayers.map(s => s.user_id))
          setBenchPool(benchPlayers.map(s => s.user_id))
          setSubstitutePool([])
        }
        setCustomPlayers(cp)
        setRoleOverrides(ro)
        setLoaded(true)
      })
      .catch(() => setLoaded(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id])

  useEffect(() => { if (editingId && nameInputRef.current) { nameInputRef.current.focus(); nameInputRef.current.select() } }, [editingId])
  useEffect(() => { if (!showAddMember) return; fetch('/api/members').then(r => r.ok ? r.json() : []).then(setMemberOptions).catch(()=>{}) }, [showAddMember])
  useEffect(() => { if (!saved) setDiscordDone(false) }, [saved])

  useEffect(() => {
    if (!discordPosted) return
    const load = () => { fetch(`${base}/confirmations`).then(r => r.ok ? r.json() : {}).then(setConfirmations).catch(() => {}) }
    load()
    const id = setInterval(load, 10_000)
    return () => clearInterval(id)
  }, [event.id, discordPosted])

  useEffect(() => {
    if (!contextMenu) return
    const close = () => setContextMenu(null)
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [contextMenu])

  useEffect(() => {
    if (!showAddSquad) return
    const close = (e: MouseEvent) => {
      const ref = showAddSquad === 'axis' ? addAxisRef : addAlliesRef
      if (ref.current && !ref.current.contains(e.target as Node)) setShowAddSquad(null)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [showAddSquad])

  useEffect(() => {
    if (!configSquadId) return
    const close = (e: MouseEvent) => {
      if (configRef.current && !configRef.current.contains(e.target as Node)) { setConfigSquadId(null); setConfigButtonRect(null) }
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [configSquadId])

  // ── Squad operations ──
  const addSquadFromPreset = (preset: PresetDef, side: 'axis' | 'allies') => {
    const id = `sq${Date.now()}`
    setSquads(prev => [...prev, { id, name: preset.label, type: preset.type, icon: preset.icon, side, slots: makeSlots(preset.defaultSlots) }])
    setShowAddSquad(null); setSaved(false)
  }

  const removeSquad = (squadId: string) => {
    const sq = squads.find(s => s.id === squadId)
    if (!sq) return
    const toUnassign: string[] = []
    const toBench: string[] = []
    for (const sl of sq.slots) {
      if (!sl.userId) continue
      const p = playerMap[sl.userId]
      if (p && BENCH_CLASSES.has(p.class_name)) toBench.push(sl.userId)
      else toUnassign.push(sl.userId)
    }
    setUnassigned(prev => [...prev, ...toUnassign])
    setBenchPool(prev => [...prev, ...toBench])
    setSquads(prev => prev.filter(s => s.id !== squadId))
    setSaved(false)
  }

  const moveSquadInSide = (squadId: string, dir: -1 | 1) => {
    setSquads(prev => {
      const sq = prev.find(s => s.id === squadId)
      if (!sq) return prev
      const sideList = prev.filter(s => s.side === sq.side)
      const sideIdx = sideList.findIndex(s => s.id === squadId)
      const newSideIdx = sideIdx + dir
      if (newSideIdx < 0 || newSideIdx >= sideList.length) return prev
      const fullIdx  = prev.findIndex(s => s.id === squadId)
      const fullNIdx = prev.findIndex(s => s.id === sideList[newSideIdx].id)
      const arr = [...prev];
      [arr[fullIdx], arr[fullNIdx]] = [arr[fullNIdx], arr[fullIdx]]
      return arr
    })
    setSaved(false)
  }

  const startEdit  = (sq: Squad) => { setEditingId(sq.id); setEditName(sq.name) }
  const commitEdit = () => {
    if (!editingId) return
    setSquads(prev => prev.map(sq => sq.id === editingId ? { ...sq, name: editName.trim() || sq.name } : sq))
    setEditingId(null)
  }

  const adjustSlotCount = (squadId: string, role: string, delta: number) => {
    setSquads(prev => prev.map(sq => {
      if (sq.id !== squadId) return sq
      const roleSlots = sq.slots.filter(s => s.role === role)
      if (delta > 0) {
        const newSlot: Slot = { id: `s${Date.now()}_${Math.random().toString(36).slice(2)}`, role, userId: null }
        return { ...sq, slots: [...sq.slots, newSlot] }
      } else if (delta < 0 && roleSlots.length > 0) {
        const lastIdx = sq.slots.map((s,i) => s.role === role ? i : -1).filter(i => i >= 0).at(-1)!
        const lastSlot = sq.slots[lastIdx]
        if (lastSlot.userId) {
          const p = playerMap[lastSlot.userId]
          if (p && BENCH_CLASSES.has(p.class_name)) setBenchPool(prev => [...prev, lastSlot.userId!])
          else setUnassigned(prev => [...prev, lastSlot.userId!])
        }
        return { ...sq, slots: sq.slots.filter((_,i) => i !== lastIdx) }
      }
      return sq
    }))
    setSaved(false)
  }

  const fillSlot = (userId: string, from: string | null, fromSlotId: string | undefined, toSquadId: string, toSlotId: string) => {
    if (from === null) setUnassigned(prev => prev.filter(id => id !== userId))
    else if (from === 'bench') setBenchPool(prev => prev.filter(id => id !== userId))
    else if (from === 'substitute') setSubstitutePool(prev => prev.filter(id => id !== userId))
    else setSquads(prev => prev.map(sq => sq.id === from
      ? { ...sq, slots: sq.slots.map(sl => sl.id === fromSlotId ? { ...sl, userId: null } : sl) }
      : sq))
    setSquads(prev => prev.map(sq => sq.id === toSquadId
      ? { ...sq, slots: sq.slots.map(sl => sl.id === toSlotId ? { ...sl, userId } : sl) }
      : sq))
    setSaved(false)
  }

  const fillFirstEmptySlot = (userId: string, from: string | null, fromSlotId: string | undefined, toSquadId: string) => {
    const sq = squads.find(s => s.id === toSquadId)
    if (!sq) return
    const emptySlot = sq.slots.find(sl => !sl.userId)
    if (!emptySlot) return
    fillSlot(userId, from, fromSlotId, toSquadId, emptySlot.id)
  }

  const returnToPool = (userId: string, fromSquadId: string, fromSlotId: string) => {
    setSquads(prev => prev.map(sq => sq.id === fromSquadId
      ? { ...sq, slots: sq.slots.map(sl => sl.id === fromSlotId ? { ...sl, userId: null } : sl) }
      : sq))
    const p = playerMap[userId]
    if (p && BENCH_CLASSES.has(p.class_name)) setBenchPool(prev => [...prev, userId])
    else setUnassigned(prev => [...prev, userId])
    setSaved(false)
  }

  const handleDropToSlot  = (toSquadId: string, toSlotId: string) => { if (!dragging) return; fillSlot(dragging.userId, dragging.from, dragging.slotId, toSquadId, toSlotId); setDragging(null); setDragOver(null) }
  const handleDropToSquad = (toSquadId: string) => { if (!dragging) return; fillFirstEmptySlot(dragging.userId, dragging.from, dragging.slotId, toSquadId); setDragging(null); setDragOver(null) }
  const handleDropToPool  = (pool: 'unassigned' | 'bench' | 'substitute') => {
    if (!dragging) return
    const { userId, from, slotId } = dragging
    if (from === null && pool === 'unassigned') { setDragging(null); setDragOver(null); return }
    if (from === 'bench' && pool === 'bench')   { setDragging(null); setDragOver(null); return }
    if (from === 'substitute' && pool === 'substitute') { setDragging(null); setDragOver(null); return }
    if (from === null) setUnassigned(prev => prev.filter(id => id !== userId))
    else if (from === 'bench') setBenchPool(prev => prev.filter(id => id !== userId))
    else if (from === 'substitute') setSubstitutePool(prev => prev.filter(id => id !== userId))
    else setSquads(prev => prev.map(sq => sq.id === from
      ? { ...sq, slots: sq.slots.map(sl => sl.id === slotId ? { ...sl, userId: null } : sl) }
      : sq))
    if (pool === 'bench') setBenchPool(prev => [...prev, userId])
    else if (pool === 'substitute') setSubstitutePool(prev => [...prev, userId])
    else setUnassigned(prev => [...prev, userId])
    setDragging(null); setDragOver(null); setSaved(false)
  }

  const toggleRole = (userId: string, roleId: string) => {
    setRoleOverrides(prev => {
      const cur = prev[userId] ?? []
      const upd = cur.includes(roleId) ? cur.filter(r => r !== roleId) : [...cur, roleId]
      return { ...prev, [userId]: upd }
    }); setSaved(false)
  }

  const openContextMenu = (e: React.MouseEvent, userId: string) => { e.preventDefault(); e.stopPropagation(); setContextMenu({ userId, x: e.clientX, y: e.clientY }) }

  const addMember = (member: MemberOption) => {
    if (playerMap[member.id]) return
    const custom: SignUp = {
      user_id: member.id, name: member.display_name || member.username,
      class_name: '', class_emote_id: null, spec_name: null, spec_emote_id: null,
      is_active: true, avatar_url: member.avatar_url, in_roster: false,
    }
    setCustomPlayers(prev => [...prev, custom])
    setUnassigned(prev => [...prev, member.id])
    setShowAddMember(false); setMemberSearch(''); setSaved(false)
  }

  const removeCustomPlayer = (userId: string) => {
    setCustomPlayers(prev => prev.filter(p => p.user_id !== userId))
    setUnassigned(prev => prev.filter(id => id !== userId))
    setBenchPool(prev => prev.filter(id => id !== userId))
    setSubstitutePool(prev => prev.filter(id => id !== userId))
    setSquads(prev => prev.map(sq => ({ ...sq, slots: sq.slots.map(sl => sl.userId === userId ? { ...sl, userId: null } : sl) })))
    setSaved(false)
  }

  const saveConfig = async () => {
    setSaving(true)
    try {
      await fetch(`${base}/squads`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ squads, custom_players: customPlayers, role_overrides: roleOverrides, substitute_players: substitutePool }),
      })
      setSaved(true)
    } finally { setSaving(false) }
  }

  const sendToDiscord = async () => {
    setDiscordSending(true); setDiscordDone(false)
    try {
      if (!saved) {
        await fetch(`${base}/squads`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ squads, custom_players: customPlayers, role_overrides: roleOverrides, substitute_players: substitutePool }),
        })
        setSaved(true)
      }
      const method = discordPosted ? 'PATCH' : 'POST'
      const res = await fetch(`${base}/discord-post`, { method })
      if (res.ok) { setDiscordPosted(true); setDiscordDone(true) }
    } finally { setDiscordSending(false) }
  }

  const filteredMembers = memberOptions.filter(m => {
    if (playerMap[m.id]) return false
    const q = memberSearch.toLowerCase()
    const label = (m.display_name || m.username || '').toLowerCase()
    return !q || label.includes(q) || m.username.toLowerCase().includes(q)
  })

  const passiveSorted = [
    ...passivePlayers.filter(s => s.class_name === 'Tentative'),
    ...passivePlayers.filter(s => s.class_name !== 'Tentative'),
  ]

  const axisSquads   = squads.filter(sq => sq.side === 'axis')
  const alliesSquads = squads.filter(sq => sq.side === 'allies')

  const CLASS_ORDER: Record<string, number> = {
    Inf: 1, infantry: 1, Tank: 2, armour: 2, tank: 2,
    Recon: 3, recon: 3, Comm: 4, commander: 4, Arty: 5, artillery: 5, Streamer: 6,
  }

  if (!loaded) return <div className="flex items-center justify-center h-full text-sm text-muted-foreground">Loading…</div>

  return (
    <div className="relative flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-5 py-3.5 border-b border-white/10 shrink-0">
        <div className="min-w-0">
          <p className="text-xs font-bold tracking-widest text-foreground/80 mb-0.5">EVENT CONFIGURATION</p>
          <h2 className="text-sm font-bold leading-tight truncate">{event.title}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{event.start_time ? formatDate(event.start_time) : event.date}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => setShowAddMember(true)}>
            <UserPlus className="h-3 w-3" /> Add Member
          </Button>
          <Button size="sm" className="h-7 gap-1.5 text-xs" onClick={saveConfig} disabled={saving} variant={saved ? 'outline' : 'default'}>
            {saved ? <><Check className="h-3 w-3" /> Saved</> : <><Save className="h-3 w-3" /> Save</>}
          </Button>
          <Button size="sm" onClick={sendToDiscord} disabled={discordSending}
            title={discordPosted ? 'Update Discord embed' : 'Send event config to Discord channel'}
            className={`h-7 gap-1.5 text-xs ${discordDone ? 'bg-transparent border border-white/20 text-white/70 hover:bg-white/5' : 'bg-[#5865F2] hover:bg-[#4752C4] text-white border-0'}`}
            variant="default">
            {discordSending
              ? <><RefreshCw className="h-3 w-3 animate-spin" /> {discordPosted ? 'Updating…' : 'Sending…'}</>
              : discordDone
                ? <><Check className="h-3 w-3" /> {discordPosted ? 'Updated' : 'Sent'}</>
                : discordPosted
                  ? <><RefreshCw className="h-3 w-3" /> Update</>
                  : <><DiscordIcon className="h-3.5 w-3.5" /> Send</>
            }
          </Button>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => { setShowAddMember(false); setMemberSearch('') }}>
          <div className="bg-card border border-white/15 rounded-md w-80 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <p className="text-sm font-semibold">Add Member</p>
              <button onClick={() => { setShowAddMember(false); setMemberSearch('') }} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-3">
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input autoFocus value={memberSearch} onChange={e => setMemberSearch(e.target.value)} placeholder="Search members…"
                  className="w-full rounded-md border border-white/15 bg-white/5 pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary/50" />
              </div>
              <div className="max-h-60 overflow-y-auto space-y-0.5">
                {filteredMembers.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No members found</p>}
                {filteredMembers.map(m => {
                  const label = m.display_name || m.username
                  return (
                    <button key={m.id} onClick={() => addMember(m)}
                      className="w-full flex items-center gap-2.5 rounded-sm px-2.5 py-1.5 hover:bg-white/10 transition-colors text-left">
                      {m.avatar_url ? <img src={m.avatar_url} className="h-7 w-7 rounded-full object-cover shrink-0" /> :
                        <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-xs shrink-0">{label[0]?.toUpperCase()}</div>}
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{label}</p>
                        <p className="text-[10px] text-muted-foreground">{m.team}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right-click context menu */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onMouseDown={() => setContextMenu(null)} />
          <div className="fixed z-50 bg-card border border-white/15 rounded-md shadow-2xl p-2"
            style={{ left: contextMenu.x, top: contextMenu.y }} onMouseDown={e => e.stopPropagation()}>
            <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 px-1 pb-1.5">EXTRA ROLES</p>
            <div className="grid grid-cols-4 gap-1">
              {EXTRA_ROLE_OPTIONS.map(role => {
                const active = (roleOverrides[contextMenu.userId] ?? []).includes(role.id)
                return (
                  <button key={role.id} onClick={() => toggleRole(contextMenu.userId, role.id)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-sm transition-colors min-w-14 ${active ? 'bg-primary/20 border border-primary/40' : 'border border-transparent hover:bg-white/10'}`}>
                    <img src={role.emoji} className="h-6 w-6 object-contain" onError={e=>{(e.target as HTMLImageElement).style.display='none'}} />
                    <span className="text-[9px] text-muted-foreground leading-none text-center">{role.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* Fixed slot config popup */}
      {configSquadId && configButtonRect && (() => {
        const sq = squads.find(s => s.id === configSquadId)
        if (!sq) return null
        const configRules = SQUAD_TYPE_CONFIGS[sq.type] ?? []
        return (
          <div ref={configRef}
            className="fixed z-50 bg-card border border-white/15 rounded-md shadow-2xl p-3 min-w-44"
            style={{ left: configButtonRect.right - 176, top: configButtonRect.bottom + 4 }}
            onMouseDown={e => e.stopPropagation()}>
            <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 mb-2">SLOT CONFIG</p>
            <div className="space-y-1.5">
              {configRules.map(rule => {
                const count = sq.slots.filter(s => s.role === rule.role).length
                return (
                  <div key={rule.role} className="flex items-center gap-2">
                    <SlotIcon role={rule.role} />
                    <span className="text-xs text-muted-foreground flex-1">{rule.label}</span>
                    {rule.fixed ? (
                      <span className="text-xs text-muted-foreground/50">{count}</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button onClick={() => adjustSlotCount(sq.id, rule.role, -1)} disabled={count <= (rule.min ?? 0)}
                          className="w-5 h-5 rounded flex items-center justify-center bg-white/10 hover:bg-white/20 disabled:opacity-30 text-xs">−</button>
                        <span className="text-xs w-4 text-center">{count}</span>
                        <button onClick={() => adjustSlotCount(sq.id, rule.role, 1)} disabled={count >= (rule.max ?? 99)}
                          className="w-5 h-5 rounded flex items-center justify-center bg-white/10 hover:bg-white/20 disabled:opacity-30 text-xs">+</button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: unassigned panel */}
        <div className="w-52 shrink-0 border-r border-white/10 flex flex-col overflow-hidden">
          <div className="shrink-0 px-3 pt-2.5 pb-1.5">
            <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 mb-1.5">UNASSIGNED ({unassigned.length})</p>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground/50 pointer-events-none" />
              <input value={unassignedSearch} onChange={e => setUnassignedSearch(e.target.value)} placeholder="Search…"
                className="w-full rounded-md border border-white/10 bg-white/5 pl-6 pr-2 py-1 text-xs outline-none focus:border-primary/40 placeholder:text-muted-foreground/30" />
            </div>
          </div>
          <div onDragOver={e=>{e.preventDefault();setDragOver('__unassigned__')}} onDragLeave={()=>setDragOver(null)}
            onDrop={()=>handleDropToPool('unassigned')}
            className={`flex-1 overflow-y-auto px-2 pb-1 space-y-1 min-h-0 transition-colors ${dragOver==='__unassigned__'?'bg-primary/5':''}`}>
            {unassigned.length === 0 && <p className="text-xs text-muted-foreground/30 text-center py-4">All assigned</p>}
            {(() => {
              const q = unassignedSearch.toLowerCase()
              return [...unassigned]
                .sort((a, b) => {
                  const pa = playerMap[a], pb = playerMap[b]
                  if (!pa || !pb) return 0
                  return (CLASS_ORDER[pa.class_name] ?? 99) - (CLASS_ORDER[pb.class_name] ?? 99)
                })
                .filter(uid => !q || (playerMap[uid]?.name.toLowerCase().includes(q)))
                .map(uid => {
                  const p = playerMap[uid]; if (!p) return null
                  const isCustom = customPlayers.some(cp => cp.user_id === uid)
                  return (
                    <UnassignedRow key={uid} player={p} isCustom={isCustom}
                      confirmStatus={confirmations[uid]}
                      onDragStart={() => setDragging({ userId: uid, from: null })}
                      onRemoveCustom={isCustom ? () => removeCustomPlayer(uid) : undefined} />
                  )
                })
            })()}
          </div>

          {/* Substitute pool — always visible, manually managed */}
          <div className="shrink-0 border-t border-white/10">
            <p className="text-[10px] font-semibold tracking-widest text-amber-400/70 px-3 py-2">YEDEK ({substitutePool.length})</p>
            <div onDragOver={e=>{e.preventDefault();setDragOver('substitute')}} onDragLeave={()=>setDragOver(null)}
              onDrop={()=>handleDropToPool('substitute')}
              className={`px-2 pb-2 space-y-1 min-h-[36px] max-h-40 overflow-y-auto transition-colors ${dragOver==='substitute'?'bg-amber-500/10 border border-dashed border-amber-400/40 rounded-md mx-1':''}`}>
              {substitutePool.length === 0 && dragOver !== 'substitute' && (
                <p className="text-[10px] text-muted-foreground/25 text-center py-2">Yedek için sürükleyin</p>
              )}
              {substitutePool.map(uid => {
                const p = playerMap[uid]; if (!p) return null
                return <UnassignedRow key={uid} player={p} confirmStatus={confirmations[uid]} onDragStart={() => setDragging({ userId: uid, from: 'substitute' })} />
              })}
            </div>
          </div>

          {event.sign_ups.some(s => BENCH_CLASSES.has(s.class_name)) && (
            <div className="shrink-0 border-t border-white/10">
              <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 px-3 py-2">BENCH ({benchPool.length})</p>
              <div onDragOver={e=>{e.preventDefault();setDragOver('bench')}} onDragLeave={()=>setDragOver(null)}
                onDrop={()=>handleDropToPool('bench')}
                className={`px-2 pb-2 space-y-1 max-h-40 overflow-y-auto transition-colors ${dragOver==='bench'?'bg-primary/5':''}`}>
                {benchPool.map(uid => {
                  const p = playerMap[uid]; if (!p) return null
                  return <UnassignedRow key={uid} player={p} onDragStart={() => setDragging({ userId: uid, from: 'bench' })} />
                })}
              </div>
            </div>
          )}

          {passiveSorted.length > 0 && (
            <div className="shrink-0 border-t border-white/10 max-h-44 overflow-y-auto">
              {passiveSorted.filter(p => p.class_name === 'Tentative').length > 0 && (
                <>
                  <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/40 px-3 py-1.5">TENTATIVE</p>
                  <div className="px-2 space-y-0.5 pb-1">
                    {passiveSorted.filter(p => p.class_name === 'Tentative').map(p => <PassiveRow key={p.user_id} player={p} />)}
                  </div>
                </>
              )}
              {passiveSorted.filter(p => p.class_name !== 'Tentative').length > 0 && (
                <>
                  <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/30 px-3 py-1.5">ABSENT</p>
                  <div className="px-2 space-y-0.5 pb-2">
                    {passiveSorted.filter(p => p.class_name !== 'Tentative').map(p => <PassiveRow key={p.user_id} player={p} />)}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* AXIS column */}
        <div className="flex-1 flex flex-col overflow-hidden border-r border-white/10">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 shrink-0 bg-red-950/20">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold tracking-widest text-red-400">AXIS</span>
              <span className="text-[10px] text-muted-foreground/50">
                {axisSquads.reduce((s, sq) => s + sq.slots.filter(sl => sl.userId).length, 0)} players
              </span>
            </div>
            <div className="relative" ref={addAxisRef}>
              <Button size="sm" variant="outline" className="h-6 gap-1 text-xs border-red-500/30 hover:border-red-500/60" onClick={() => setShowAddSquad(v => v === 'axis' ? null : 'axis')}>
                <ChevronDown className="h-3 w-3" /> Add Squad
              </Button>
              {showAddSquad === 'axis' && (
                <div className="absolute right-0 top-full mt-1 z-30 bg-card border border-white/15 rounded-md shadow-2xl overflow-hidden min-w-40">
                  {SQUAD_PRESETS.map(p => (
                    <button key={p.type} onClick={() => addSquadFromPreset(p, 'axis')}
                      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-white/10 transition-colors text-left">
                      <img src={p.icon} className="h-5 w-5 object-contain" onError={e=>{(e.target as HTMLImageElement).style.display='none'}} />
                      <span className="text-sm">{p.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <div className="flex flex-wrap gap-3 content-start">
              {axisSquads.map((sq, idx) => (
                <SquadCard
                  key={sq.id} squad={sq} squads={squads}
                  playerMap={playerMap} roleOverrides={roleOverrides}
                  confirmations={confirmations} dragOver={dragOver}
                  isEditing={editingId === sq.id} editName={editName} nameInputRef={nameInputRef}
                  isFirst={idx === 0} isLast={idx === axisSquads.length - 1}
                  showConfig={configSquadId === sq.id} configRef={configRef}
                  onStartEdit={() => startEdit(sq)} onCommitEdit={commitEdit} onEditName={setEditName}
                  onRemove={() => removeSquad(sq.id)}
                  onMoveLeft={() => moveSquadInSide(sq.id, -1)} onMoveRight={() => moveSquadInSide(sq.id, 1)}
                  onToggleConfig={(e: React.MouseEvent) => {
                    if (configSquadId === sq.id) { setConfigSquadId(null); setConfigButtonRect(null) }
                    else { setConfigSquadId(sq.id); setConfigButtonRect((e.currentTarget as HTMLElement).getBoundingClientRect()) }
                  }}
                  onDragStartPlayer={(uid, slotId) => setDragging({ userId: uid, from: sq.id, slotId })}
                  onDragOverSlot={(slotId) => setDragOver(slotId)}
                  onDragLeaveSlot={() => setDragOver(null)}
                  onDropToSlot={(slotId) => handleDropToSlot(sq.id, slotId)}
                  onDragOverSquad={() => setDragOver(`squad_${sq.id}`)}
                  onDragLeaveSquad={() => setDragOver(null)}
                  onDropToSquad={() => handleDropToSquad(sq.id)}
                  onDoubleClickSlot={(uid, slotId) => returnToPool(uid, sq.id, slotId)}
                  onContextMenu={openContextMenu}
                />
              ))}
              {axisSquads.length === 0 && <p className="text-sm text-muted-foreground/40 py-8 px-4">Click "Add Squad" to add AXIS squads</p>}
            </div>
          </div>
        </div>

        {/* ALLIES column */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 shrink-0 bg-blue-950/20">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold tracking-widest text-blue-400">ALLIES</span>
              <span className="text-[10px] text-muted-foreground/50">
                {alliesSquads.reduce((s, sq) => s + sq.slots.filter(sl => sl.userId).length, 0)} players
              </span>
            </div>
            <div className="relative" ref={addAlliesRef}>
              <Button size="sm" variant="outline" className="h-6 gap-1 text-xs border-blue-500/30 hover:border-blue-500/60" onClick={() => setShowAddSquad(v => v === 'allies' ? null : 'allies')}>
                <ChevronDown className="h-3 w-3" /> Add Squad
              </Button>
              {showAddSquad === 'allies' && (
                <div className="absolute right-0 top-full mt-1 z-30 bg-card border border-white/15 rounded-md shadow-2xl overflow-hidden min-w-40">
                  {SQUAD_PRESETS.map(p => (
                    <button key={p.type} onClick={() => addSquadFromPreset(p, 'allies')}
                      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-white/10 transition-colors text-left">
                      <img src={p.icon} className="h-5 w-5 object-contain" onError={e=>{(e.target as HTMLImageElement).style.display='none'}} />
                      <span className="text-sm">{p.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <div className="flex flex-wrap gap-3 content-start">
              {alliesSquads.map((sq, idx) => (
                <SquadCard
                  key={sq.id} squad={sq} squads={squads}
                  playerMap={playerMap} roleOverrides={roleOverrides}
                  confirmations={confirmations} dragOver={dragOver}
                  isEditing={editingId === sq.id} editName={editName} nameInputRef={nameInputRef}
                  isFirst={idx === 0} isLast={idx === alliesSquads.length - 1}
                  showConfig={configSquadId === sq.id} configRef={configRef}
                  onStartEdit={() => startEdit(sq)} onCommitEdit={commitEdit} onEditName={setEditName}
                  onRemove={() => removeSquad(sq.id)}
                  onMoveLeft={() => moveSquadInSide(sq.id, -1)} onMoveRight={() => moveSquadInSide(sq.id, 1)}
                  onToggleConfig={(e: React.MouseEvent) => {
                    if (configSquadId === sq.id) { setConfigSquadId(null); setConfigButtonRect(null) }
                    else { setConfigSquadId(sq.id); setConfigButtonRect((e.currentTarget as HTMLElement).getBoundingClientRect()) }
                  }}
                  onDragStartPlayer={(uid, slotId) => setDragging({ userId: uid, from: sq.id, slotId })}
                  onDragOverSlot={(slotId) => setDragOver(slotId)}
                  onDragLeaveSlot={() => setDragOver(null)}
                  onDropToSlot={(slotId) => handleDropToSlot(sq.id, slotId)}
                  onDragOverSquad={() => setDragOver(`squad_${sq.id}`)}
                  onDragLeaveSquad={() => setDragOver(null)}
                  onDropToSquad={() => handleDropToSquad(sq.id)}
                  onDoubleClickSlot={(uid, slotId) => returnToPool(uid, sq.id, slotId)}
                  onContextMenu={openContextMenu}
                />
              ))}
              {alliesSquads.length === 0 && <p className="text-sm text-muted-foreground/40 py-8 px-4">Click "Add Squad" to add ALLIES squads</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── UnassignedRow ────────────────────────────────────────────────────────────

function UnassignedRow({ player, isCustom, confirmStatus, onDragStart, onRemoveCustom }: {
  player: SignUp; isCustom?: boolean; confirmStatus?: string; onDragStart: () => void; onRemoveCustom?: () => void
}) {
  return (
    <div draggable onDragStart={onDragStart}
      className="group flex items-center gap-2 rounded-md px-2 py-1.5 bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10 cursor-grab active:cursor-grabbing select-none transition-colors">
      <GripVertical className="h-3 w-3 text-muted-foreground/25 shrink-0" />
      {confirmStatus !== undefined && <ConfirmDot status={confirmStatus} />}
      {player.avatar_url
        ? <img src={player.avatar_url} className="h-5 w-5 rounded-full object-cover shrink-0" />
        : <div className="h-5 w-5 rounded-full bg-muted shrink-0 flex items-center justify-center text-[9px]">{player.name[0]?.toUpperCase()}</div>}
      <span className="text-xs font-medium truncate flex-1 min-w-0">{player.name}</span>
      <DiscordClassIcon su={player} />
      {isCustom && onRemoveCustom && (
        <button onClick={e => { e.stopPropagation(); onRemoveCustom() }}
          className="opacity-0 group-hover:opacity-100 text-muted-foreground/40 hover:text-destructive transition-all ml-0.5">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}

function PassiveRow({ player }: { player: SignUp }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-white/[0.035] text-muted-foreground/50 select-none">
      <DiscordClassIcon su={player} />
      <span className="text-xs truncate">{player.name}</span>
    </div>
  )
}

// ─── SquadCard ────────────────────────────────────────────────────────────────

interface SquadCardProps {
  squad: { id: string; name: string; type: string; icon: string; slots: Slot[] }
  squads: { id: string; name: string; type: string; icon: string; slots: Slot[] }[]
  playerMap: Record<string,SignUp>; roleOverrides: Record<string,string[]>; confirmations: Record<string,string>
  dragOver: string | null; isEditing: boolean; editName: string; nameInputRef: React.RefObject<HTMLInputElement|null>
  isFirst: boolean; isLast: boolean; showConfig: boolean; configRef?: React.RefObject<HTMLDivElement|null>
  onStartEdit: () => void; onCommitEdit: () => void; onEditName: (v: string) => void
  onRemove: () => void; onMoveLeft: () => void; onMoveRight: () => void
  onToggleConfig: (e: React.MouseEvent) => void
  onDragStartPlayer: (uid: string, slotId: string) => void
  onDragOverSlot: (slotId: string) => void; onDragLeaveSlot: () => void; onDropToSlot: (slotId: string) => void
  onDragOverSquad: () => void; onDragLeaveSquad: () => void; onDropToSquad: () => void
  onDoubleClickSlot: (uid: string, slotId: string) => void
  onContextMenu: (e: React.MouseEvent, uid: string) => void
}

function SquadCard({
  squad, playerMap, roleOverrides, confirmations, dragOver,
  isEditing, editName, nameInputRef, isFirst, isLast, showConfig,
  onStartEdit, onCommitEdit, onEditName, onRemove, onMoveLeft, onMoveRight,
  onToggleConfig, onDragStartPlayer, onDragOverSlot, onDragLeaveSlot, onDropToSlot,
  onDragOverSquad, onDragLeaveSquad, onDropToSquad, onDoubleClickSlot, onContextMenu,
}: SquadCardProps) {
  const squadDragOver = dragOver === `squad_${squad.id}`
  const configRules   = SQUAD_TYPE_CONFIGS[squad.type] ?? []
  return (
    <div onDragOver={e => { e.preventDefault(); onDragOverSquad() }} onDragLeave={onDragLeaveSquad} onDrop={onDropToSquad}
      className={`min-w-52 w-fit rounded-md border transition-colors ${squadDragOver ? 'border-primary/40 bg-primary/5' : 'border-white/15 bg-white/5'}`}>
      <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-white/10">
        <img src={squad.icon} className="h-4 w-4 object-contain shrink-0" onError={e=>{(e.target as HTMLImageElement).style.display='none'}} />
        {isEditing ? (
          <>
            <input ref={nameInputRef} value={editName} onChange={e => onEditName(e.target.value)}
              onBlur={onCommitEdit} onKeyDown={e => { if (e.key === 'Enter') onCommitEdit() }}
              className="flex-1 text-xs font-semibold bg-transparent border-b border-primary outline-none py-0.5 min-w-0" />
            <button onClick={onCommitEdit} className="text-primary p-0.5 shrink-0"><Check className="h-3 w-3" /></button>
          </>
        ) : (
          <>
            <button onClick={onStartEdit} className="flex-1 text-xs font-semibold text-left hover:text-primary transition-colors min-w-0 whitespace-nowrap">{squad.name}</button>
            <button onClick={onStartEdit} className="text-muted-foreground/30 hover:text-muted-foreground p-0.5 shrink-0"><Pencil className="h-3 w-3" /></button>
          </>
        )}
        {configRules.some(r => !r.fixed) && (
          <button onClick={e => { e.stopPropagation(); onToggleConfig(e) }}
            className={`p-0.5 shrink-0 transition-colors ${showConfig ? 'text-primary' : 'text-muted-foreground/30 hover:text-muted-foreground'}`}>
            <Settings className="h-3 w-3" />
          </button>
        )}
        <button onClick={onMoveLeft}  disabled={isFirst} className="text-muted-foreground/25 hover:text-muted-foreground disabled:opacity-10 p-0.5 shrink-0"><ChevronLeft  className="h-3 w-3" /></button>
        <button onClick={onMoveRight} disabled={isLast}  className="text-muted-foreground/25 hover:text-muted-foreground disabled:opacity-10 p-0.5 shrink-0"><ChevronRight className="h-3 w-3" /></button>
        <button onClick={onRemove} className="text-muted-foreground/25 hover:text-destructive p-0.5 shrink-0"><X className="h-3 w-3" /></button>
      </div>
      <div className="p-1.5 space-y-1">
        {squad.slots.length === 0 && <p className="text-[10px] text-muted-foreground/25 text-center py-3">No slots configured</p>}
        {squad.slots.map(slot => {
          const player = slot.userId ? playerMap[slot.userId] : null
          return (
            <SlotRow key={slot.id} slot={slot} player={player ?? null}
              confirmStatus={slot.userId ? confirmations[slot.userId] : undefined}
              extraRoles={slot.userId ? (roleOverrides[slot.userId] ?? []) : []}
              isDragOver={dragOver === slot.id}
              onDragStart={slot.userId ? () => onDragStartPlayer(slot.userId!, slot.id) : undefined}
              onDragOver={!slot.userId ? (e) => { e.preventDefault(); e.stopPropagation(); onDragOverSlot(slot.id) } : undefined}
              onDragLeave={!slot.userId ? () => onDragLeaveSlot() : undefined}
              onDrop={!slot.userId ? (e) => { e.stopPropagation(); onDropToSlot(slot.id) } : undefined}
              onDoubleClick={slot.userId ? () => onDoubleClickSlot(slot.userId!, slot.id) : undefined}
              onContextMenu={slot.userId ? (e) => onContextMenu(e, slot.userId!) : undefined}
            />
          )
        })}
      </div>
    </div>
  )
}

// ─── SlotRow ──────────────────────────────────────────────────────────────────

function SlotRow({ slot, player, confirmStatus, extraRoles, isDragOver, onDragStart, onDragOver, onDragLeave, onDrop, onDoubleClick, onContextMenu }: {
  slot: Slot; player: SignUp | null; confirmStatus?: string; extraRoles: string[]; isDragOver: boolean
  onDragStart?: () => void; onDragOver?: (e: React.DragEvent) => void
  onDragLeave?: () => void; onDrop?: (e: React.DragEvent) => void
  onDoubleClick?: () => void; onContextMenu?: (e: React.MouseEvent) => void
}) {
  return (
    <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      className={`flex items-center gap-1.5 rounded-md px-1.5 py-1.5 transition-colors border ${
        player ? 'bg-white/5 border-white/10' : isDragOver ? 'bg-primary/10 border-primary/40 border-dashed' : 'border-white/10 border-dashed'
      }`}>
      <SlotIcon role={slot.role} dim={!player} />
      {player ? (
        <div draggable onDragStart={onDragStart} onDoubleClick={onDoubleClick} onContextMenu={onContextMenu}
          title="Right-click for extra roles · Double-click to unassign"
          className="flex-1 flex items-center gap-1.5 min-w-0 cursor-grab active:cursor-grabbing select-none">
          <GripVertical className="h-3 w-3 text-muted-foreground/20 shrink-0" />
          {confirmStatus !== undefined && <ConfirmDot status={confirmStatus} />}
          {player.avatar_url
            ? <img src={player.avatar_url} className="h-4 w-4 rounded-full object-cover shrink-0" />
            : <div className="h-4 w-4 rounded-full bg-muted shrink-0 flex items-center justify-center text-[8px]">{player.name[0]?.toUpperCase()}</div>}
          <span className="text-xs font-medium truncate flex-1 min-w-0">{player.name}</span>
          {extraRoles.length > 0 && (
            <div className="flex items-center gap-0.5 shrink-0">
              {extraRoles.map(r => <ExtraRoleIcon key={r} roleId={r} />)}
            </div>
          )}
        </div>
      ) : (
        <span className="text-[11px] text-muted-foreground/25 flex-1">Empty</span>
      )}
    </div>
  )
}
